import React, { useState } from "react";
import { Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import Input from "../../ui/Input";
import SelectObject from "../../ui/SelectObject";
import Textarea from "../../ui/Textarea";
import IconButton from "../../ui/IconButton";
import { faEye, faEyeSlash, faTrash } from "@fortawesome/free-solid-svg-icons";

interface FieldState {
  field: any;
  index: number;
  fields: any[];
  setFields: any;
  numPreviews: any;
  setNumPreviews: any;
}

const CreateNoteTypeFieldCard = ({
  field,
  index,
  fields,
  setFields,
  numPreviews,
  setNumPreviews,
}: FieldState) => {
  const [label, setLabel] = useState<string>(field?.label);
  const [type, setType] = useState<string>(field?.type);
  const [newOption, setNewOption] = useState<string>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [allowSaveOption, setAllowSaveOption] = useState<boolean>(false);
  const [previewEnabled, setPreviewEnabled] = useState<boolean>(!!field?.preview) || false;

  const fieldOptions = [
    { key: "checkbox", name: "Checkbox" },
    { key: "date", name: "Date" },
    { key: "text", name: "Text" },
    { key: "multi-line text", name: "Multi-Line Text" },
    { key: "dropdown", name: "Dropdown" },
    { key: "time", name: "Time" }
  ];

  const updateLabel = (e: any) => {
    setLabel(e.target.value);
    setFields((prevFields: any) => prevFields.map((f: any) => (f.id === field?.id ? { ...f, label: e.target.value } : f)));
  };

  const updateType = (e: any) => {
    setType(e.target.value);

    if (e.target.value === "dropdown") {
      setFields((prevFields: any) => prevFields.map((f: any) => (f.id === field?.id ? { ...f, type: e.target.value } : f)));
    } else {
      setFields((prevFields: any) => prevFields.map((f: any) => (f.id === field?.id ? { ...f, type: e.target.value, options: [] } : f)));
    }
  };

  const togglePreview = () => {
    const newPreview = !previewEnabled;
    if (numPreviews >= 2 && newPreview) return;
    setPreviewEnabled(newPreview);

    setFields((prevFields: any) =>
      prevFields.map((f: any) =>
        f.id === field?.id ? { ...f, preview: newPreview } : f
      )
    );

    setNumPreviews((prev: number) => (newPreview ? prev + 1 : Math.max(0, prev - 1)));
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    setFields((prevFields: any) => prevFields.filter((f: any) => f.id !== field?.id));
    if (field?.preview) setNumPreviews((prev: number) => Math.max(0, prev - 1));
    setIsDeleting(false);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  const updateNewOption = (e: any) => {
    setNewOption(e.target.value);
    setAllowSaveOption(true);
  };

  const onClickDeleteOption = (optionName: string) => {
    setFields((prevFields: any) =>
      prevFields.map((f: any) =>
        f.id === field?.id ? { ...f, options: f.options.filter((option: any) => option.name !== optionName) } : f
      )
    );
  };

  const onClickAddOptionSubmit = (e: any) => {
    e?.preventDefault();
    onClickAddOption();
  };

  const onClickAddOption = () => {
    const option = {
      name: newOption,
    };

    setFields((prevFields: any) =>
      prevFields.map((f: any) => (f.id === field?.id ? { ...f, options: [...(f.options || []), option] } : f))
    );

    setNewOption("");
    setAllowSaveOption(false);
  };

  const renderDelete = () => {
    return (
      <Row className="my-3">
        <Stack direction="horizontal" gap={3}>
          <Stack style={{ justifyContent: "center" }} className="col-6">
            <Card.Subtitle>Are you sure you want to delete {label} field?</Card.Subtitle>
          </Stack>
          <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
            Confirm Delete
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
            Cancel
          </Button>
        </Stack>
      </Row>
    );
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <>
          <Row className="mb-2 g-3">
            <Col xs={6}>
              <Textarea label="Label" value={label} onUpdate={(e: any) => updateLabel(e)} rows={1} css={""} />
            </Col>
            <Col>
              <SelectObject label="Type" value={type} options={[...fieldOptions]} onUpdate={(e: any) => updateType(e)} />
            </Col>
            <Col xs={"auto"} className="d-flex align-items-center pe-3">
              <Stack direction="horizontal" gap={3} className="me-3">
                {(field?.type === "dropdown" || field?.type === "text" || field?.type === "multi-line text") && (
                  <>
                    <IconButton
                      icon={previewEnabled ? faEye : faEyeSlash}
                      color={previewEnabled ? "app-green-text" : "text-muted"}
                      tooltipText={!previewEnabled && numPreviews >= 2 ? "Max Selected" : previewEnabled ? "Disable Preview" : "Allow Preview"}
                      disabled={!previewEnabled && numPreviews >= 2}
                      onClickButton={togglePreview}
                    />
                    <div className="vr my-2" />
                  </>
                )}
                <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete Field" icon={faTrash} />
              </Stack>
            </Col>
          </Row>
          {type === "dropdown" && (
            <>
              {field?.options.length > 0 && (
                <>
                  <Row>
                    <h6 className="cursor-pointer ms-1">Choices</h6>
                  </Row>
                  {field?.options?.map((item: any, index: number) => (
                    <Row key={index}>
                      <Col xs={6}>
                        <Card key={item.name} className="mb-2">
                          <Card.Body>
                            <Stack direction="horizontal" gap={3}>
                              <Stack gap={2} className="align-self-center">
                                <Card.Subtitle className="text-muted">
                                  <small>{item.name}</small>
                                </Card.Subtitle>
                              </Stack>
                              <IconButton
                                onClickButton={() => onClickDeleteOption(item.name)}
                                tooltipText={"Delete Option"}
                                icon={faTrash}
                              />
                            </Stack>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
              <Form onSubmit={(e: any) => onClickAddOptionSubmit(e)}>
                <Row className="mb-2">
                  <Col xs={6}>
                    <Input label="New Choice" type="text" value={newOption} onUpdate={(e: any) => updateNewOption(e)} hasFocus={true} />
                  </Col>
                  <Col xs={"auto"} className="d-flex align-items-center">
                    <Button
                      className="app-secondary-color button me-3"
                      disabled={allowSaveOption ? false : true}
                      onClick={() => onClickAddOption()}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </>
      )}
    </>
  );
};

export default CreateNoteTypeFieldCard;
